import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getScanById } from '../services/scanService';
import Button from '../components/common/Button';
import Loader from '../components/common/Loader';
import { FaCheck, FaArrowRight, FaRedo } from 'react-icons/fa';
import './TrilogyResults.css';

const TrilogyResults = () => {
    const { scanId } = useParams();
    const [scan, setScan] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchScan = async () => {
            try {
                const data = await getScanById(scanId);
                setScan(data);
            } catch (err) {
                console.error('Error fetching scan results:', err);
                setError('Failed to load results. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        if (scanId) {
            fetchScan();
        }
    }, [scanId]);

    if (loading) return <Loader />;

    if (error || !scan) {
        return (
            <div className="trilogy-results-container">
                <div className="error-state">
                    <h2>Error Loading Results</h2>
                    <p>{error || 'Result not found'}</p>
                    <Link to="/trilogy-assessment">
                        <Button>Take Assessment Again</Button>
                    </Link>
                </div>
            </div>
        );
    }

    const { computed } = scan;
    const { normalizedTotal, zone, rawScores, recommendations } = computed;

    const getZoneColor = (z) => {
        switch (z) {
            case 'GREEN': return 'green';
            case 'YELLOW': return 'yellow';
            case 'RED': return 'red';
            default: return 'green';
        }
    };

    const getZoneTitle = (z) => {
        switch (z) {
            case 'GREEN': return 'Ready for Growth';
            case 'YELLOW': return 'Needs Optimization';
            case 'RED': return 'Critical Attention Needed';
            default: return 'Assessment Complete';
        }
    };

    const getZoneDescription = (z) => {
        switch (z) {
            case 'GREEN': return 'Your organization is well-positioned for sustainable growth. Focus on maintaining momentum and exploring new opportunities.';
            case 'YELLOW': return 'There are some gaps in your organizational structure or processes. Addressing these areas will unlock significant potential.';
            case 'RED': return 'Foundational elements need immediate attention. Prioritize stabilizing core operations before pursuing aggressive growth.';
            default: return '';
        }
    };

    return (
        <div className="trilogy-results-container">
            <motion.div
                className="trilogy-results-header"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
            >
                <h1>Assessment Results</h1>
                <p>Here is the breakdown of your organization's performance</p>
            </motion.div>

            <motion.div
                className="trilogy-score-card"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
            >
                <div className={`trilogy-score-circle ${getZoneColor(zone)}`}>
                    <span className="score-value">{normalizedTotal}</span>
                    <span className="score-label">/ 75</span>
                </div>

                <div className="trilogy-zone-info">
                    <h2 style={{ color: getZoneColor(zone) === 'green' ? '#00ff9d' : getZoneColor(zone) === 'yellow' ? '#ffd700' : '#ff4d4d' }}>
                        {getZoneTitle(zone)}
                    </h2>
                    <p>{getZoneDescription(zone)}</p>
                </div>
            </motion.div>

            <div className="trilogy-pillars-grid">
                {Object.entries(rawScores).map(([pillar, score], index) => (
                    <motion.div
                        key={pillar}
                        className="pillar-card"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 + (index * 0.1) }}
                    >
                        <h3>{pillar}</h3>
                        <div className="pillar-score">{score}</div>
                    </motion.div>
                ))}
            </div>

            {recommendations && recommendations.length > 0 && (
                <motion.div
                    className="trilogy-recommendations"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                >
                    <h3>Key Recommendations</h3>
                    <div className="recommendations-list">
                        {recommendations.map((rec, index) => (
                            <div key={index} className="recommendation-item">
                                <div className="rec-icon">
                                    <FaCheck />
                                </div>
                                <div className="rec-content">
                                    <h4>{rec.title || 'Strategic Recommendation'}</h4>
                                    <p>{rec.text || rec}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </motion.div>
            )}

            <div className="trilogy-actions">
                <Link to="/contact">
                    <Button size="large">
                        Book Your LEAP <FaArrowRight style={{ marginLeft: '8px' }} />
                    </Button>
                </Link>
                <Link to="/trilogy-assessment">
                    <Button variant="secondary" size="large">
                        Retake Assessment <FaRedo style={{ marginLeft: '8px' }} />
                    </Button>
                </Link>
            </div>
        </div>
    );
};

export default TrilogyResults;
